/* RebalancePanel — toont terugkerende BKR-afwijking-patronen per weekdag,
 * met per pattern een 1-klik "verschuiving inplannen" flow (via de standaard
 * overstaffed-coach dialog, of gewoon een informatieve suggestie).
 */
import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CalendarRange, Repeat, ChevronRight, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function RebalancePanel({ weken = 4 }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refFrom, setRefFrom] = useState("");
  const [refTo, setRefTo] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/rooster/rebalance/suggesties?weken=${weken}`);
      setItems(r.data?.items || []);
      setRefFrom(r.data?.reference_from || "");
      setRefTo(r.data?.reference_to || "");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Kon suggesties niet laden");
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, [weken]);

  if (loading) {
    return (
      <Card className="p-4 border border-slate-200" data-testid="rebalance-panel-loading">
        <div className="flex items-center gap-2 text-slate-500 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" /> Analyseer patronen …
        </div>
      </Card>
    );
  }

  if (items.length === 0) return null;

  return (
    <Card className="p-4 border-2 border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 mb-4"
          data-testid="rebalance-panel">
      <div className="flex items-start gap-3">
        <div className="rounded-full bg-amber-100 p-2.5 flex-shrink-0">
          <Repeat className="h-5 w-5 text-amber-700" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-amber-900">
              {items.length === 1
                ? "1 terugkerend BKR-patroon gedetecteerd"
                : `${items.length} terugkerende BKR-patronen gedetecteerd`}
            </span>
            <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200">
              Structurele shift
            </span>
          </div>
          <p className="text-xs text-amber-700 mt-1 flex items-center gap-1">
            <CalendarRange className="h-3 w-3" />
            Analyse periode: {refFrom} t/m {refTo}
          </p>
          <ul className="mt-3 space-y-2">
            {items.slice(0, 5).map((it, i) => (
              <li key={i}
                  className="bg-white/60 border border-amber-200 rounded p-2 text-sm"
                  data-testid={`rebalance-item-${i}`}>
                <div className="flex items-center gap-2">
                  <ChevronRight className="h-3.5 w-3.5 text-amber-500 flex-shrink-0" />
                  <span className="font-semibold text-amber-900">{it.group_name}</span>
                  <span className="text-xs text-amber-700 capitalize">· {it.weekdag_naam}</span>
                  <span className="ml-auto text-xs tabular-nums text-amber-800">
                    {it.weken_getroffen}× · gem. {it.gemiddelde_min} min
                  </span>
                </div>
                <p className="text-xs text-amber-900/80 mt-1 leading-snug">
                  {it.suggestion_text}
                </p>
              </li>
            ))}
          </ul>
          {items.length > 5 && (
            <p className="text-xs text-amber-700 mt-2">+ {items.length - 5} meer …</p>
          )}
          <div className="mt-3 flex gap-2">
            <Button size="sm" variant="ghost" onClick={load}
                    data-testid="rebalance-refresh"
                    className="h-7 text-amber-700 hover:bg-amber-100">
              Ververs analyse
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
}
